import { Theme, SxProps } from '@mui/material/styles';

/**
 * Centralized styling system for PHEPlateTypeManagement component
 */
export interface PHEPlateTypeManagementStyles {
  container: SxProps<Theme>;
  contentWrapper: SxProps<Theme>;
  headerCell: SxProps<Theme>;
  tableContainer: SxProps<Theme>;
  table: SxProps<Theme>;
  tableRow: SxProps<Theme>;
  tableCell: SxProps<Theme>;
  dialogTitle: SxProps<Theme>;
  loadingContainer: SxProps<Theme>;
  formSection: SxProps<Theme>;
  formLabel: SxProps<Theme>;
  positionChip: SxProps<Theme>;
  positionsContainer: SxProps<Theme>;
  infoMessage: SxProps<Theme>;
  actionButtonsContainer: SxProps<Theme>;
  noResultsMessage: SxProps<Theme>;
}

/**
 * Creates styling object for PHEPlateTypeManagement component
 */
export const createPHEPlateTypeManagementStyles = (theme: Theme): PHEPlateTypeManagementStyles => ({
  container: {
    padding: 3,
    marginTop: 2
  },
  contentWrapper: {
    width: '100%',
    maxWidth: '98%',
    mx: 'auto',
    overflowX: 'visible',
    overflowY: 'visible'
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.background.paper,
    padding: '12px 16px',
    position: 'sticky',
    top: 0,
    zIndex: 1
  },
  tableContainer: {
    mt: 3,
    width: '100%',
    boxShadow: theme.shadows[2],
    borderRadius: 1
  },
  table: {
    tableLayout: 'fixed',
    width: '100%'
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  tableCell: {
    padding: '8px 16px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    mb: 2
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
    width: '100%'
  },
  formSection: {
    my: 2
  },
  formLabel: {
    fontWeight: 'bold',
    mb: 1
  },
  positionChip: {
    margin: 0.5,
    cursor: 'pointer'
  },
  positionsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    my: 1,
    p: 1,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 1,
    overflowY: 'auto'
  },
  infoMessage: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    mt: 1
  },
  actionButtonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    mb: 2
  },
  noResultsMessage: {
    p: 4,
    width: '100%',
    textAlign: 'center',
    backgroundColor: theme.palette.grey[50],
    border: '1px dashed',
    borderColor: theme.palette.grey[300]
  }
});

/**
 * Individual style components for specific UI elements
 */
export const pheChipStyles = {
  hygienic: {
    borderColor: 'info.main',
    color: 'info.dark',
    bgcolor: 'info.lighter'
  },
  industrial: {
    borderColor: 'warning.main',
    color: 'warning.dark',
    bgcolor: 'warning.lighter'
  }
};

export const actionButtonStyles = {
  createButton: {
    whiteSpace: 'nowrap',
    padding: '6px 26px !important',
    textTransform: 'none',
    boxShadow: 1
  },
  searchField: {
    width: '300px',
    height: '36px'
  },
  dialogActions: {
    p: 2,
    borderTop: (theme: Theme) => `1px solid ${theme.palette.divider}`
  }
};

export const layoutStyles = {
  headerActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 4,
    mt: 1
  },
  formRow: {
    direction: { xs: 'column', md: 'row' } as const,
    spacing: 2
  },
  checkboxRow: {
    direction: 'row' as const,
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  checkboxGroup: {
    direction: 'row' as const,
    spacing: 2
  }
}; 